"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sortByUserBalance = exports.isValidSolanaAddress = exports.base64ToJson = exports.jsonToBase64 = exports.hasNumericValue = exports.splitIntoChunks = exports.useOutsideClick = exports.useDebounce = exports.detectedSeparator = exports.isMobile = exports.useReactiveEventListener = exports.toLamports = exports.fromLamports = exports.readableValue = exports.shortenAddress = exports.formatNumber = exports.numberFormatter = void 0;
const web3_js_1 = require("@solana/web3.js");
const decimal_js_1 = __importDefault(require("decimal.js"));
const react_1 = require("react");
const tokenTags_1 = require("./tokenTags");
const userLocale = typeof window !== 'undefined'
    ? navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.language
    : 'en-US';
exports.numberFormatter = new Intl.NumberFormat(userLocale, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 9,
});
const getDecimalCount = (value) => {
    const parts = value.split('.');
    return parts.length > 1 ? parts[1].length : 0;
};
exports.formatNumber = {
    format: (val, precision) => {
        if (!val) {
            return '';
        }
        // Use the default precision if not provided
        const defaultDecimals = getDecimalCount(val.toString());
        // format it against user locale
        const numberFormatter = new Intl.NumberFormat(userLocale, {
            maximumFractionDigits: precision !== null && precision !== void 0 ? precision : defaultDecimals,
        });
        return numberFormatter.format(val.toString());
    },
};
function shortenAddress(address, chars = 4) {
    return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}
exports.shortenAddress = shortenAddress;
function readableValue(lamportsAmount, decimals) {
    return new decimal_js_1.default(lamportsAmount.toString())
        .div(Math.pow(10, decimals))
        .toDP(decimals, decimal_js_1.default.ROUND_DOWN)
        .toFixed();
}
exports.readableValue = readableValue;
function fromLamports(lamportsAmount, decimals) {
    return new decimal_js_1.default(lamportsAmount.toString())
        .div(Math.pow(10, decimals))
        .toDP(decimals, decimal_js_1.default.ROUND_DOWN)
        .toNumber();
}
exports.fromLamports = fromLamports;
function toLamports(lamportsAmount, decimals) {
    return new decimal_js_1.default(lamportsAmount.toString())
        .mul(Math.pow(10, decimals))
        .floor()
        .toNumber();
}
exports.toLamports = toLamports;
// https://usehooks.com/useEventListener/
function useReactiveEventListener(eventName, handler, element = typeof window !== 'undefined' ? window : null) {
    // Create a ref that stores handler
    const savedHandler = (0, react_1.useRef)();
    // Update ref.current value if handler changes.
    // This allows our effect below to always get latest handler ...
    // ... without us needing to pass it in effect deps array ...
    // ... and potentially cause effect to re-run every render.
    (0, react_1.useEffect)(() => {
        savedHandler.current = handler;
    }, [handler]);
    (0, react_1.useEffect)(() => {
        if (typeof window !== 'undefined') {
            // Make sure element supports addEventListener
            // On
            const isSupported = element && element.addEventListener;
            if (!isSupported)
                return;
            // Create event listener that calls handler function stored in ref
            const eventListener = (event) => typeof savedHandler.current === 'function' && savedHandler.current(event);
            // Add event listener
            element.addEventListener(eventName, eventListener);
            // Remove event listener on cleanup
            return () => {
                element.removeEventListener(eventName, eventListener);
            };
        }
    }, [eventName, element]);
}
exports.useReactiveEventListener = useReactiveEventListener;
const isMobile = () => typeof window !== 'undefined' && screen && screen.width <= 480;
exports.isMobile = isMobile;
exports.detectedSeparator = exports.formatNumber.format('1.1').substring(1, 2);
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = (0, react_1.useState)(value);
    (0, react_1.useEffect)(() => {
        // Update debounced value after delay
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        // Cancel the timeout if value changes (also on delay change or unmount)
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}
exports.useDebounce = useDebounce;
function useOutsideClick(ref, handler) {
    (0, react_1.useEffect)(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mouseup', listener);
        return () => {
            document.removeEventListener('mouseup', listener);
        };
    }, [ref, handler]);
}
exports.useOutsideClick = useOutsideClick;
function splitIntoChunks(array, size) {
    return Array.apply(0, new Array(Math.ceil(array.length / size))).map((_, index) => array.slice(index * size, (index + 1) * size));
}
exports.splitIntoChunks = splitIntoChunks;
const hasNumericValue = (amount) => {
    if (amount && !Number.isNaN(Number(amount))) {
        return true;
    }
    return false;
};
exports.hasNumericValue = hasNumericValue;
function jsonToBase64(object) {
    try {
        const json = JSON.stringify(object);
        return Buffer.from(json).toString('base64');
    }
    catch (error) {
        console.log(error);
        return null;
    }
}
exports.jsonToBase64 = jsonToBase64;
function base64ToJson(base64String) {
    try {
        const json = Buffer.from(base64String, 'base64').toString();
        return JSON.parse(json);
    }
    catch (error) {
        console.log(error);
        return null;
    }
}
exports.base64ToJson = base64ToJson;
function isValidSolanaAddress(address) {
    try {
        new web3_js_1.PublicKey(address);
        return true;
    }
    catch (error) {
        console.error('Invalid Solana address:', error);
        return false;
    }
}
exports.isValidSolanaAddress = isValidSolanaAddress;
/**
 * Sorts search results based on user balance and other criteria
 * @param searchResults - Array of search assets to sort
 * @param options - Sorting options including balances and preferences
 * @returns Sorted array of search assets
 */
const sortByUserBalance = (searchResults, balances) => {
    // Create maps for efficient lookups
    const userBalanceMap = new Map();
    const userUsdValueMap = new Map();
    // Calculate user balances and USD values
    Object.entries(balances)
        .filter(([_, item]) => item.uiAmount > 0)
        .forEach(([mint, item]) => {
        const tokenInfo = searchResults.find((token) => token.id === mint);
        if (!tokenInfo)
            return;
        const amount = item.uiAmount;
        userBalanceMap.set(mint, amount);
        const tokenPrice = tokenInfo.usdPrice || 0;
        if (tokenPrice) {
            const usdValue = new decimal_js_1.default(amount).mul(tokenPrice);
            if (usdValue.greaterThan(0)) {
                userUsdValueMap.set(mint, usdValue);
            }
        }
    });
    // Deduplicate tokens by ID
    const deduplicatedResults = (() => {
        const map = new Map();
        searchResults.forEach((item) => {
            if (!map.has(item.id)) {
                map.set(item.id, item);
            }
        });
        return Array.from(map.values());
    })();
    return deduplicatedResults.sort((t1, t2) => {
        var _a, _b, _c, _d;
        // 1. USD value comparison (highest first)
        const t1UsdValue = userUsdValueMap.get(t1.id);
        const t2UsdValue = userUsdValueMap.get(t2.id);
        if (t1UsdValue && t2UsdValue) {
            const usdComparison = t2UsdValue.cmp(t1UsdValue);
            if (usdComparison !== 0)
                return usdComparison;
        }
        else if (t1UsdValue && !t2UsdValue) {
            return -1; // t1 has USD value, t2 doesn't
        }
        else if (!t1UsdValue && t2UsdValue) {
            return 1; // t2 has USD value, t1 doesn't
        }
        // 2. Balance comparison (highest first)
        const t1Balance = userBalanceMap.get(t1.id);
        const t2Balance = userBalanceMap.get(t2.id);
        if (t1Balance && t2Balance) {
            const balanceComparison = new decimal_js_1.default(t2Balance).cmp(t1Balance);
            if (balanceComparison !== 0)
                return balanceComparison;
        }
        else if (t1Balance && !t2Balance) {
            return -1; // t1 has balance, t2 doesn't
        }
        else if (!t1Balance && t2Balance) {
            return 1; // t2 has balance, t1 doesn't
        }
        // 3. User token prioritization
        const t1HasBalance = userBalanceMap.has(t1.id);
        const t2HasBalance = userBalanceMap.has(t2.id);
        if (t1HasBalance && !t2HasBalance)
            return -1;
        if (!t1HasBalance && t2HasBalance)
            return 1;
        // 4. Volume-based scoring
        let t1Score = 0;
        let t2Score = 0;
        const t1Volume = (((_a = t1.stats24h) === null || _a === void 0 ? void 0 : _a.buyVolume) || 0) + (((_b = t1.stats24h) === null || _b === void 0 ? void 0 : _b.sellVolume) || 0);
        const t2Volume = (((_c = t2.stats24h) === null || _c === void 0 ? void 0 : _c.buyVolume) || 0) + (((_d = t2.stats24h) === null || _d === void 0 ? void 0 : _d.sellVolume) || 0);
        // Higher volume gets higher score
        if (t1Volume > t2Volume)
            t1Score += 1;
        if (t2Volume > t1Volume)
            t2Score += 1;
        // 5. Known token prioritization
        if ((0, tokenTags_1.checkIsUnknownToken)(t1))
            t1Score -= 2;
        if ((0, tokenTags_1.checkIsUnknownToken)(t2))
            t2Score -= 2;
        return t2Score - t1Score;
    });
};
exports.sortByUserBalance = sortByUserBalance;
